import React, { useEffect, useState, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBuilding, faTimes, faImage, faUpload, faCrop } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { getApiEndpoint } from '../../../config/api';
// Removed ReactCrop - using custom positioning system instead
import './PropertyProfileModal.css';

const initialForm = {
  name: '',
  address: '',
  image_url: ''
};

export default function PropertyProfileModal({ isOpen, onClose, propertyId, initialValues = initialForm, onUpdated, readOnly = false }) {
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  
  // Image positioning states
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePosition, setImagePosition] = useState({ x: 0, y: 0, scale: 1 });
  const [showPositioner, setShowPositioner] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const imgRef = useRef(null);
  const canvasRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setForm({
        name: initialValues.name || '',
        address: initialValues.address || '',
        image_url: initialValues.image_url || ''
      });
      setPreviewUrl('');
      setShowPositioner(false);
      setSelectedImage(null);
      setImagePosition({ x: 0, y: 0, scale: 0.8 });
    }
  }, [isOpen, initialValues]);

  // Handle mouse and wheel events for Facebook-style positioner
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !showPositioner) return;

    const handleWheelEvent = (e) => {
      e.preventDefault();
      const delta = e.deltaY > 0 ? 0.95 : 1.05;
      setImagePosition(prev => ({
        ...prev,
        scale: Math.max(0.3, Math.min(4, prev.scale * delta))
      }));
    };

    const handleMouseDownEvent = (e) => {
      e.preventDefault();
      setIsDragging(true);
      const rect = container.getBoundingClientRect();
      setDragStart({
        x: e.clientX - rect.left - imagePosition.x,
        y: e.clientY - rect.top - imagePosition.y
      });
    };

    const handleMouseMoveEvent = (e) => {
      if (!isDragging) return;
      e.preventDefault();
      
      const rect = container.getBoundingClientRect();
      setImagePosition(prev => ({
        ...prev,
        x: e.clientX - rect.left - dragStart.x,
        y: e.clientY - rect.top - dragStart.y
      }));
    };

    const handleMouseUpEvent = () => {
      setIsDragging(false);
    };

    container.addEventListener('wheel', handleWheelEvent, { passive: false });
    container.addEventListener('mousedown', handleMouseDownEvent);
    document.addEventListener('mousemove', handleMouseMoveEvent);
    document.addEventListener('mouseup', handleMouseUpEvent);
    
    return () => {
      container.removeEventListener('wheel', handleWheelEvent);
      container.removeEventListener('mousedown', handleMouseDownEvent);
      document.removeEventListener('mousemove', handleMouseMoveEvent);
      document.removeEventListener('mouseup', handleMouseUpEvent);
    };
  }, [showPositioner, isDragging, dragStart.x, dragStart.y, imagePosition.x, imagePosition.y]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (readOnly) return;
    setSaving(true);
    try {
      const response = await fetch(getApiEndpoint(`/api/properties/${propertyId}`), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(form)
      });

      if (!response.ok) {
        let details = '';
        try {
          const errJson = await response.json();
          details = errJson?.details || errJson?.error || '';
        } catch (_) {
          try {
            const text = await response.text();
            details = text;
          } catch (_) {}
        }
        console.error('Update property failed:', response.status, details);
        throw new Error(details || 'Failed to update property')
      }
      const updated = await response.json();
      toast.success('Property updated successfully');
      if (onUpdated) onUpdated(updated);
      onClose();
    } catch (err) {
      console.error('Error updating property:', err);
      toast.error('Failed to update property');
    } finally {
      setSaving(false);
    }
  };

  const handleImageSelect = async (file) => {
    if (!file) return;
    try {
      setUploading(true);
      const fd = new FormData();
      fd.append('image', file);
      const res = await fetch(getApiEndpoint(`/api/properties/${propertyId}/upload-image`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: fd
      });
      const contentType = res.headers.get('content-type') || '';
      let payloadRaw = await res.text();
      let data;
      try {
        data = contentType.includes('application/json') ? JSON.parse(payloadRaw) : {};
      } catch (_) {
        data = {};
      }
      if (!res.ok) {
        const message = data?.error || data?.message || payloadRaw || `HTTP ${res.status}`;
        throw new Error(message);
      }
      const imageUrl = data?.imageUrl || '';
      if (imageUrl) {
        setForm((prev) => ({ ...prev, image_url: imageUrl }));
        const fullPreview = getApiEndpoint(`/api/${imageUrl}`);
        setPreviewUrl(fullPreview);
        toast.success('Image uploaded successfully');
      }
    } catch (err) {
      console.error('Simple upload error:', err);
      const message = (err && err.message) ? err.message : 'Failed to upload image';
      toast.error(message);
    } finally {
      setUploading(false);
    }
  };

  // Mouse and wheel handling is now done in useEffect with proper event listener options

  const getPositionedImage = (image, position, frameSize, cropArea, containerSize) => {
    const canvas = canvasRef.current;
    
    if (!canvas || !image) {
      console.error('Canvas or image not available');
      return;
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      console.error('Could not get canvas context');
      return;
    }

    canvas.width = frameSize.width;
    canvas.height = frameSize.height;
    
    // Clear canvas
    ctx.clearRect(0, 0, frameSize.width, frameSize.height);
    
    // Use actual container size when available
    const containerWidth = containerSize?.width || 800;
    const containerHeight = containerSize?.height || 500;
    
    // Calculate scaled image dimensions
    const scaledWidth = image.naturalWidth * position.scale;
    const scaledHeight = image.naturalHeight * position.scale;
    
    // Calculate the actual position relative to container center
    const centerX = containerWidth / 2;
    const centerY = containerHeight / 2;
    const imageX = centerX + position.x - scaledWidth / 2;
    const imageY = centerY + position.y - scaledHeight / 2;
    
    // Calculate crop area relative to the container
    const cropX = cropArea.x;
    const cropY = cropArea.y;
    const cropWidth = cropArea.width;
    const cropHeight = cropArea.height;
    
    // Calculate which part of the image to draw
    const sourceX = Math.max(0, (cropX - imageX) / position.scale);
    const sourceY = Math.max(0, (cropY - imageY) / position.scale);
    const sourceWidth = Math.min(image.naturalWidth - sourceX, cropWidth / position.scale);
    const sourceHeight = Math.min(image.naturalHeight - sourceY, cropHeight / position.scale);
    
    // Calculate destination position on canvas
    const destX = Math.max(0, imageX - cropX) * (frameSize.width / cropWidth);
    const destY = Math.max(0, imageY - cropY) * (frameSize.height / cropHeight);
    const destWidth = sourceWidth * position.scale * (frameSize.width / cropWidth);
    const destHeight = sourceHeight * position.scale * (frameSize.height / cropHeight);
    
    // Draw the cropped portion
    if (sourceWidth > 0 && sourceHeight > 0) {
      ctx.drawImage(
        image,
        sourceX, sourceY, sourceWidth, sourceHeight,
        destX, destY, destWidth, destHeight
      );
    }
  };

  const uploadPositionedImage = async () => {
    if (!imgRef.current) {
      toast.error('No image selected');
      return;
    }
    
    if (!canvasRef.current) {
      toast.error('Canvas not ready');
      return;
    }
    
    try {
      setUploading(true);
      
      // Define crop areas relative to current container
      const cw = containerRef.current?.clientWidth || 800;
      const ch = containerRef.current?.clientHeight || 500;
      const sidebarCropArea = { x: 100, y: 100, width: 248, height: 220 };
      
      // Generate the positioned image for sidebar dimensions (248x220)
      getPositionedImage(
        imgRef.current,
        imagePosition,
        { width: 248, height: 220 },
        sidebarCropArea,
        { width: cw, height: ch }
      );
      
      const canvas = canvasRef.current;
      
      // Convert canvas to blob
      canvas.toBlob(async (blob) => {
        if (!blob) {
          toast.error('Failed to process image');
          setUploading(false);
          return;
        }
        
        try {
          const fd = new FormData();
          fd.append('image', blob, 'positioned-image.jpg');
          
          const res = await fetch(getApiEndpoint(`/api/properties/${propertyId}/upload-image`), {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: fd
          });
          
          const data = await res.json();
          if (!res.ok) throw new Error(data.error || 'Upload failed');
          
          const imageUrl = data?.imageUrl || '';
          if (imageUrl) {
            setForm((prev) => ({ ...prev, image_url: imageUrl }));
            const fullPreview = getApiEndpoint(`/api/${imageUrl}`);
            setPreviewUrl(fullPreview);
            setShowPositioner(false);
            setSelectedImage(null);
            toast.success('Image uploaded successfully');
          }
        } catch (uploadErr) {
          console.error('Upload error:', uploadErr);
          toast.error('Failed to upload image');
        } finally {
          setUploading(false);
        }
      }, 'image/jpeg', 0.9);
    } catch (err) {
      console.error('Processing error:', err);
      toast.error('Failed to process image');
      setUploading(false);
    }
  };

  const onFileInputChange = (e) => handleImageSelect(e.target.files?.[0]);
  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    handleImageSelect(file);
  };
  const onDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };
  const onDragLeave = () => setDragOver(false);

  if (showPositioner) {
    // Frame definitions in container coordinate space (800x500 desktop; CSS scales on mobile below)
    const CONTAINER_WIDTH = 800;
    const CONTAINER_HEIGHT = 500;
    const sidebarFrame = { x: 100, y: 100, width: 248, height: 220 };
    const cardFrame = { x: CONTAINER_WIDTH - 100 - 220, y: 100, width: 220, height: 220 };

    const computePreviewStyle = (frame, previewSize) => {
      if (!imgRef.current) return {};
      const image = imgRef.current;

      // Scaled image dimensions
      const scaledW = image.naturalWidth * imagePosition.scale;
      const scaledH = image.naturalHeight * imagePosition.scale;

      // Image top-left within container after translate, with image anchored at container center
      const imageLeft = CONTAINER_WIDTH / 2 + imagePosition.x - scaledW / 2;
      const imageTop = CONTAINER_HEIGHT / 2 + imagePosition.y - scaledH / 2;

      // Portion of the original image that lands inside the frame
      const srcX = Math.max(0, (frame.x - imageLeft) / imagePosition.scale);
      const srcY = Math.max(0, (frame.y - imageTop) / imagePosition.scale);
      const srcW = Math.max(0, Math.min(image.naturalWidth - srcX, frame.width / imagePosition.scale));
      const srcH = Math.max(0, Math.min(image.naturalHeight - srcY, frame.height / imagePosition.scale));

      // Background-size should map original pixels to preview pixels
      const scaleX = previewSize.width / srcW;
      const scaleY = previewSize.height / srcH;

      const bgW = image.naturalWidth * scaleX;
      const bgH = image.naturalHeight * scaleY;

      // Position so that (srcX, srcY) lands at (0,0) of preview
      const bgPosX = -srcX * scaleX;
      const bgPosY = -srcY * scaleY;

      return {
        backgroundImage: `url(${selectedImage})`,
        backgroundPosition: `${bgPosX}px ${bgPosY}px`,
        backgroundSize: `${bgW}px ${bgH}px`,
        backgroundRepeat: 'no-repeat'
      };
    };

    return (
      <div className="modal-overlay" onClick={() => setShowPositioner(false)}>
        <div className="modal modern-modal facebook-style-positioner" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2><FontAwesomeIcon icon={faImage} /> Position Your Image</h2>
            <button className="close-btn" onClick={() => setShowPositioner(false)}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>

          <div className="facebook-positioner-content">
            {/* Left side - Instructions */}
            <div className="facebook-instructions">
              <div className="instruction-box">
                <p><strong>Drag to move the image</strong></p>
                <p>• Scroll to zoom in/out</p>
                <p>Position your image so the important parts are visible in the preview boxes</p>
              </div>
              
              {/* Main positioning area */}
              <div 
                className="facebook-image-container"
                ref={containerRef}
              >
                {/* Gray overlay areas */}
                <div className="facebook-overlay facebook-overlay-top"></div>
                <div className="facebook-overlay facebook-overlay-bottom"></div>
                <div className="facebook-overlay facebook-overlay-left"></div>
                <div className="facebook-overlay facebook-overlay-right"></div>
                
                {/* Draggable image */}
                <img
                  ref={imgRef}
                  src={selectedImage}
                  alt="Position preview"
                  className="facebook-draggable-image"
                  style={{
                    transform: `translate(${imagePosition.x}px, ${imagePosition.y}px) scale(${imagePosition.scale})`,
                    cursor: isDragging ? 'grabbing' : 'grab'
                  }}
                  draggable={false}
                />
                
                {/* Crop frame indicators */}
                <div className="facebook-crop-frame sidebar-frame">
                  <span className="facebook-frame-label">Sidebar View</span>
                </div>
                <div className="facebook-crop-frame property-card-frame">
                  <span className="facebook-frame-label">Property Card</span>
                </div>
              </div>
            </div>

            {/* Right side - Live Preview */}
            <div className="facebook-preview-section">
              <h3>Live Preview</h3>
              
              <div className="facebook-preview-box">
                <span className="facebook-preview-label">SIDEBAR (248PX × 220PX)</span>
                <div className="facebook-preview-image sidebar-preview-size">
                  <div 
                    className="facebook-preview-content"
                    style={computePreviewStyle(sidebarFrame, { width: 124, height: 110 })}
                  />
                </div>
              </div>

              <div className="facebook-preview-box">
                <span className="facebook-preview-label">PROPERTY CARD (220PX × 220PX)</span>
                <div className="facebook-preview-image property-card-preview-size">
                  <div 
                    className="facebook-preview-content"
                    style={computePreviewStyle(cardFrame, { width: 110, height: 110 })}
                  />
                </div>
              </div>
              
              {/* Zoom controls */}
              <div className="facebook-zoom-controls">
                <button 
                  type="button" 
                  className="facebook-zoom-btn"
                  onClick={() => setImagePosition(prev => ({ ...prev, scale: Math.max(0.5, prev.scale * 0.9) }))}
                >
                  Zoom Out
                </button>
                <button 
                  type="button" 
                  className="facebook-zoom-btn"
                  onClick={() => setImagePosition(prev => ({ ...prev, scale: Math.min(3, prev.scale * 1.1) }))}
                >
                  Zoom In
                </button>
                <button 
                  type="button" 
                  className="facebook-zoom-btn"
                  onClick={() => setImagePosition({ x: 0, y: 0, scale: 1 })}
                >
                  Reset
                </button>
              </div>
            </div>
          </div>

          <div className="facebook-positioner-actions">
            <button 
              type="button" 
              className="btn-cancel" 
              onClick={() => setShowPositioner(false)}
              disabled={uploading}
            >
              Cancel
            </button>
            <button 
              type="button" 
              className="btn-primary facebook-save-btn" 
              onClick={uploadPositionedImage}
              disabled={uploading}
            >
              {uploading ? 'Uploading...' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modern-modal property-profile-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2><FontAwesomeIcon icon={faBuilding} /> {readOnly ? 'Property Profile' : 'Edit Property'}</h2>
          <button className="close-btn" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modern-form">
          <div className="form-section">
            <div className="form-group">
              <label htmlFor="name">Property Name</label>
              <input
                type="text"
                id="name"
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder="Enter property name"
                required
                readOnly={readOnly}
                disabled={readOnly}
              />
            </div>

            <div className="form-group">
              <label htmlFor="address">Address</label>
              <input
                type="text"
                id="address"
                name="address"
                value={form.address}
                onChange={handleChange}
                placeholder="Enter property address"
                required
                readOnly={readOnly}
                disabled={readOnly}
              />
            </div>
          </div>

          <div className="form-section">
            <label>Property Image</label>
            
            {(form.image_url || previewUrl) ? (
              <div className="current-image-section">
                <div className="current-image-preview">
                  <img 
                    src={previewUrl || getApiEndpoint(`/api/${form.image_url}`)} 
                    alt="Current property" 
                  />
                </div>
                {!readOnly && (
                  <div className="image-actions">
                    <button 
                      type="button" 
                      className="btn-secondary"
                      onClick={() => document.getElementById('property-profile-file-input')?.click()}
                    >
                      <FontAwesomeIcon icon={faImage} />
                      Change Image
                    </button>
                    <div style={{ marginTop: 8, fontSize: 12, color: '#64748b' }}>
                      Recommended sizes: Sidebar 248×220, Property Card 220×220.
                    </div>
                  </div>
                )}
              </div>
            ) : !readOnly ? (
              <div
                className={`modern-dropzone ${dragOver ? 'dragover' : ''}`}
                onDragOver={onDragOver}
                onDragLeave={onDragLeave}
                onDrop={onDrop}
                onClick={() => document.getElementById('property-profile-file-input')?.click()}
              >
                <div className="dropzone-content">
                  <FontAwesomeIcon icon={faImage} className="dropzone-icon" />
                  <h3>Add Property Image</h3>
                  <p>Drag & drop an image here, or click to select</p>
                  <span className="dropzone-hint">Supports JPG, PNG, GIF up to 5MB</span>
                  <span className="dropzone-hint" style={{ display: 'block', marginTop: 6 }}>
                    Recommended sizes: Sidebar 248×220, Property Card 220×220
                  </span>
                </div>
              </div>
            ) : (
              <p style={{ color: '#64748b', fontSize: 14 }}>No image</p>
            )}
            
            {!readOnly && (
              <input
                id="property-profile-file-input"
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={onFileInputChange}
              />
            )}
          </div>

          <div className="modal-actions">
            <button type="button" className="btn-cancel" onClick={onClose} disabled={saving}>
              {readOnly ? 'Close' : 'Cancel'}
            </button>
            {!readOnly && (
              <button type="submit" className="btn-primary" disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            )}
          </div>
        </form>
        
        {/* Hidden canvas for image processing */}
        <canvas ref={canvasRef} style={{ display: 'none' }} />
      </div>
    </div>
  );
}